<template>
  <div v-if="currentState === 'edit'" class="flex flex-col gap-4">
    <div class="flex flex-col gap-1.5">
      <label class="text-sm secondary_color">Card name</label>
      <a-input
        v-model:value="editData.name"
        placeholder="Enter card name"
        class="input_medium"
      />
    </div>

    <div class="flex flex-col gap-1.5">
      <label class="text-sm secondary_color"
        >Provide custom instructions the agent should follow to generate
        response</label
      >
      <a-textarea
        v-model:value="editData.prompt"
        placeholder="Enter your prompt here..."
        :rows="12"
        class="textarea_medium"
      />
    </div>

    <div class="flex items-center justify-end gap-2 mt-2">
      <a-button class="secondary_small" @click="handleCancel">
        Cancel
      </a-button>

      <a-button class="primary_small" @click="handleSaveChanges">
        Save Changes
      </a-button>
    </div>
  </div>

  <div
    v-else-if="currentState === 'loading'"
    class="flex flex-col gap-3 items-center justify-center w-full min-h-[290px]"
  >
    <ThemeImage
      class="size-[104px]"
      :icon="imageNames.SUPER_AGI_FILLED_BOLT"
      alt="super_agi_icon"
    />

    <TypingText
      source="ai_card"
      text="Thinking"
      text-style="text-xl font-semibold"
      :number-of-dots="3"
    />
  </div>

  <div v-else-if="currentState === 'display'" class="flex flex-col gap-3">
    <iframe
      v-if="cardData.response"
      ref="responseIframe"
      class="response-iframe"
      :srcdoc="iframeContent"
      frameborder="0"
      scrolling="no"
      @load="adjustIframeHeight"
    ></iframe>

    <div
      v-if="cardData.images && cardData.images.length > 0"
      class="flex flex-col gap-2"
    >
      <img
        v-for="(imageUrl, imgIdx) in cardData.images"
        :key="imgIdx"
        :src="imageUrl"
        alt="Generated image"
        class="rounded-lg max-w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
        @click="openImageModal(imageUrl)"
      />
    </div>

    <TableView
      v-if="cardData.json_data?.table"
      :table-data="cardData.json_data"
    />

    <ChartView
      v-if="cardData.json_data?.chart"
      :chartData="cardData.json_data"
    />

    <div v-if="!showHeader" class="flex items-center justify-end gap-2 mt-2">
      <a-button class="secondary_small" @click="handleEditPrompt">
        Edit Prompt
      </a-button>
    </div>
  </div>
</template>

<script setup>
import { inject, ref, computed } from "vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import TableView from "@/src/views/chats/TableView.vue";
import ChartView from "@/src/views/chats/ChartView.vue";
import TypingText from "@/src/components/loading_components/TypingText.vue";
import store from "@/src/store";

const aiCardState = inject("aiCardState");

if (!aiCardState) {
  throw new Error("AICardBody must be used within AICard component");
}

const currentState = aiCardState.currentState;
const editData = aiCardState.editData;
const cardData = aiCardState.cardData;
const showHeader = aiCardState.showHeader;
const formatMessage = aiCardState.formatMessage;
const handleCancel = aiCardState.handleCancel;
const handleSaveChanges = aiCardState.handleSaveChanges;
const handleEditPrompt = aiCardState.handleEditPrompt;
const openImageModal = aiCardState.openImageModal;
const imageNames = aiCardState.imageNames;

const responseIframe = ref(null);

const effective_theme = computed(
  () => store.getters["theme/getEffectiveTheme"],
);
const is_dark_theme = computed(() => effective_theme.value === "DARK");
const text_color = computed(() => is_dark_theme.value ? "#ffffff" : "#000000");

const iframeStyles = computed(() => `
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      font-size: 14px;
      line-height: 1.6;
      color: ${text_color.value};
      background: transparent;
      padding: 0;
      overflow: hidden;
    }
    h1 {
      font-size: 1.5rem;
      font-weight: 700;
      margin: 0.75rem 0 0.5rem 0;
      line-height: 1.3;
    }
    h2 {
      font-size: 1.25rem;
      font-weight: 600;
      margin: 0.625rem 0 0.5rem 0;
      line-height: 1.3;
    }
    h3 {
      font-size: 1.125rem;
      font-weight: 600;
      margin: 0.5rem 0 0.375rem 0;
      line-height: 1.4;
    }
    h4, h5, h6 {
      font-size: 1rem;
      font-weight: 600;
      margin: 0.375rem 0 0.25rem 0;
      line-height: 1.4;
    }
    p {
      margin: 0.5rem 0;
    }
    ul, ol {
      margin: 0.5rem 0;
      padding-left: 1.5rem;
    }
    li {
      margin: 0.25rem 0;
    }
    a {
      color: #818cf8;
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    a:hover {
      opacity: 0.8;
    }
    strong, b {
      font-weight: 600;
    }
    em, i {
      font-style: italic;
    }
    code {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.875em;
      background: rgba(255, 255, 255, 0.1);
      padding: 0.125rem 0.375rem;
      border-radius: 4px;
    }
    pre {
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      font-size: 0.875rem;
      background: rgba(255, 255, 255, 0.08);
      padding: 0.75rem 1rem;
      border-radius: 6px;
      overflow-x: auto;
      margin: 0.5rem 0;
    }
    pre code {
      background: transparent;
      padding: 0;
      font-size: inherit;
    }
    blockquote {
      border-left: 3px solid rgba(255, 255, 255, 0.2);
      padding-left: 1rem;
      margin: 0.5rem 0;
      opacity: 0.9;
    }
    hr {
      border: none;
      border-top: 1px solid rgba(255, 255, 255, 0.12);
      margin: 1rem 0;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 0.5rem 0;
      font-size: 0.875rem;
    }
    th, td {
      border: 1px solid rgba(255, 255, 255, 0.12);
      padding: 0.5rem 0.75rem;
      text-align: left;
    }
    th {
      background: rgba(255, 255, 255, 0.04);
      font-weight: 600;
    }
    img {
      max-width: 100%;
      height: auto;
      border-radius: 6px;
    }
  </style>
`);

const isCompleteHtmlDocument = (content) => {
  if (!content) return false;
  const trimmed = content.trim().toLowerCase();
  return trimmed.startsWith("<!doctype html") || trimmed.startsWith("<html");
};

const iframeContent = computed(() => {
  const content = formatMessage(cardData.value?.response || "");

  // If response is already a complete HTML document, use it directly
  // but override the container styles to be responsive
  if (isCompleteHtmlDocument(content)) {
    // Inject responsive override styles into existing HTML
    const responsiveOverride = `
      <style>
        .container {
          width: 100% !important;
          height: auto !important;
          max-width: 100% !important;
          overflow: visible !important;
        }
        body {
          overflow: visible !important;
        }
      </style>
    `;
    // Insert before closing </head> tag
    if (content.includes("</head>")) {
      return content.replace("</head>", `${responsiveOverride}</head>`);
    }
    return content;
  }

  // Otherwise wrap with our default styles
  return `<!DOCTYPE html><html><head><meta charset="utf-8">${iframeStyles.value}</head><body>${content}</body></html>`;
});

const adjustIframeHeight = () => {
  const iframe = responseIframe.value;
  if (iframe && iframe.contentWindow) {
    try {
      const body = iframe.contentWindow.document.body;
      if (body) {
        iframe.style.height = body.scrollHeight + "px";
      }
    } catch (e) {
      console.error("Error adjusting iframe height:", e);
    }
  }
};
</script>

<style scoped>
.response-iframe {
  width: 100%;
  min-height: 50px;
  border: none;
  background: transparent;
}
</style>
